import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { Switch } from '../../../../../components/ui/Switch';
import { KitComponentsEditor } from '../components/KitComponentsEditor';
import {
  createProduct,
  fetchProductOptions,
  updateProduct,
} from '../../../../../features/catalog/catalogThunks';
import {
  EMPTY_KIT_COMPONENT,
  ROTATION_OPTIONS,
} from '../../../../../features/catalog/catalogConstants';
import {
  buildProductPayload,
  productFormFrom,
  productProblem,
  uomHint,
} from '../../../../../features/catalog/catalogUtils';

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider pt-2">
    {children}
  </h3>
);

/**
 * A yes/no question as a select rather than a switch: there are three in a row
 * and each asks "does it control X?", which reads better with the word in it.
 */
const YesNoSelect = ({ id, label, value, onChange, yesLabel = 'Sí', disabled }) => (
  <Field label={label} htmlFor={id}>
    <select
      id={id}
      data-testid={id}
      value={String(value)}
      onChange={(event) => onChange(event.target.value === 'true')}
      disabled={disabled}
      className={inputClass}
    >
      <option value="true">{yesLabel}</option>
      <option value="false">No</option>
    </select>
  </Field>
);

/**
 * Creates a product in a client's catalog, or edits one.
 *
 * What is set here rules the whole warehouse: a product that controls lot is
 * not received without one. The button says what is missing while it is
 * disabled, and the server's own sentence is shown when it refuses.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.product - The product being edited; `null` for a new one.
 * @param {Array<{value: number, label: string}>} props.clientOptions - Whose
 *   catalog a new product can go into. With a single client it is not asked.
 * @param {number|null} props.defaultCompanyId - The client the listing is
 *   filtered by, offered as the starting choice.
 * @param {(message: string) => void} props.onSaved - Told what happened, in a sentence.
 */
export const ProductFormModal = ({
  isOpen,
  onClose,
  product,
  clientOptions,
  defaultCompanyId,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { saving, options, optionsCompanyId, loadingOptions } = useSelector(
    (state) => state.catalog
  );

  const [form, setForm] = useState(() => productFormFrom(product));
  const [companyId, setCompanyId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const isEditing = Boolean(product);
  const onlyClientId = clientOptions.length === 1 ? clientOptions[0].value : null;

  // Seeded on each opening, from the row that asked for it.
  useEffect(() => {
    if (!isOpen) return;
    setForm(productFormFrom(product));
    setCompanyId(product ? product.company : defaultCompanyId ?? onlyClientId);
    setSaveError(null);
  }, [isOpen, product, defaultCompanyId, onlyClientId]);

  // A kit is made of the same client's products, so they are asked for once
  // both are known — and again if the client changes.
  useEffect(() => {
    if (isOpen && form.is_kit && companyId) dispatch(fetchProductOptions(companyId));
  }, [isOpen, form.is_kit, companyId, dispatch]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const companyName = isEditing
    ? product.company_name
    : clientOptions.find((option) => option.value === companyId)?.label;

  // No kit inside a kit, and a product cannot be a component of itself.
  const offered =
    optionsCompanyId === companyId
      ? options.filter((candidate) => !candidate.is_kit && candidate.id !== product?.id)
      : [];
  // What the kit already has stays readable whatever the list brings: a
  // component taken out of service is no longer offered, and it is still there.
  const offeredIds = new Set(offered.map((candidate) => candidate.id));
  const kitCandidates = [
    ...offered,
    ...(product?.components || [])
      .filter((component) => !offeredIds.has(component.product))
      .map((component) => ({ id: component.product, sku: component.sku, name: component.name })),
  ];

  // The unit rewrites the meaning of every movement the product ever had, so
  // it is closed as soon as there is stock. The server is the one that checks
  // the history; this only keeps the field from inviting the change.
  const isUnitLocked = isEditing && Number(product.stock_total) !== 0;

  const problem = productProblem(form, companyId);

  const handleKitToggle = (event) => {
    const is_kit = event.target.checked;
    set({
      is_kit,
      // Ticking the box opens on one empty row instead of on nothing.
      components:
        is_kit && form.components.length === 0 ? [{ ...EMPTY_KIT_COMPONENT }] : form.components,
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    const values = buildProductPayload(form);

    const action = await dispatch(
      isEditing
        ? updateProduct({ productId: product.id, values })
        : createProduct({ companyId, values })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onSaved(isEditing ? 'Producto actualizado.' : `Se creó ${values.sku}.`);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEditing ? `Editar ${product.sku}` : 'Nuevo producto'}
      testId="product-form-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {companyName || 'Elige el cliente dueño del producto'}
      </p>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {saveError && (
          <div
            data-testid="product-form-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        {/* The owner is chosen once, at creation: a product does not change hands. */}
        {!isEditing && clientOptions.length > 1 && (
          <FilterableSelect
            variant="form"
            testId="product-client"
            label="Cliente"
            value={companyId}
            // Components of the previous client would not be this one's.
            onChange={(value) => {
              setCompanyId(value);
              set({ components: form.is_kit ? [{ ...EMPTY_KIT_COMPONENT }] : [] });
            }}
            options={clientOptions}
            placeholder="Seleccionar..."
            searchPlaceholder="Buscar cliente..."
            disabled={saving}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="SKU"
            htmlFor="product-sku"
            hint="Único dentro de ese cliente. Dos clientes distintos sí pueden repetirlo."
          >
            <input
              id="product-sku"
              type="text"
              data-testid="product-sku"
              value={form.sku}
              onChange={(event) => set({ sku: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>

          <Field
            label="Código de barras (EAN)"
            htmlFor="product-barcode"
            hint="Lo que se escanea al pickear. Si está mal, el escaneo rechaza el producto correcto."
          >
            <input
              id="product-barcode"
              type="text"
              data-testid="product-barcode"
              value={form.barcode}
              onChange={(event) => set({ barcode: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Nombre" htmlFor="product-name">
          <input
            id="product-name"
            type="text"
            data-testid="product-name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>

        <SectionTitle>Cómo se lleva en el kardex</SectionTitle>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <Field
            label="Unidad base"
            htmlFor="product-uom"
            hint={
              isUnitLocked
                ? 'No se puede cambiar: el producto ya tiene stock, y reescribiría el significado de todo su historial.'
                : 'No se podrá cambiar una vez que el producto tenga movimientos.'
            }
          >
            <input
              id="product-uom"
              type="text"
              data-testid="product-uom"
              value={form.base_uom}
              onChange={(event) => set({ base_uom: event.target.value })}
              disabled={saving || isUnitLocked}
              className={inputClass}
            />
          </Field>

          <div className="sm:pt-8">
            <Switch
              checked={form.allows_fractional_qty}
              onChange={(allows_fractional_qty) => set({ allows_fractional_qty })}
              label="Admite decimales"
              disabled={saving}
              testId="product-fractional"
            />
            <p className="text-text-muted text-xs mt-2">{uomHint(form.allows_fractional_qty)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <YesNoSelect
            id="product-lot"
            label="Control de lote"
            value={form.lot_controlled}
            onChange={(lot_controlled) => set({ lot_controlled })}
            disabled={saving}
          />
          <YesNoSelect
            id="product-expiry"
            label="Control de vencimiento"
            value={form.expiry_controlled}
            onChange={(expiry_controlled) => set({ expiry_controlled })}
            disabled={saving}
          />
          <YesNoSelect
            id="product-serial"
            label="Control de serie"
            value={form.serial_controlled}
            onChange={(serial_controlled) => set({ serial_controlled })}
            disabled={saving}
          />
        </div>
        <p className="text-text-muted text-xs -mt-2">
          Con lote o vencimiento en Sí, la recepción obliga a capturar ese dato al contar. Con
          lote, el picking sugiere primero lo que vence antes. La serie hoy solo se declara: su
          captura llega con la trazabilidad por serie.
        </p>

        <SectionTitle>Kit</SectionTitle>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="product-is-kit"
            checked={form.is_kit}
            onChange={handleKitToggle}
            disabled={saving}
            className="w-4 h-4 rounded border-border text-ninja-green focus:ring-ninja-green bg-white dark:bg-[#1a1a1a] cursor-pointer"
          />
          <span className="text-sm text-ninja-dark dark:text-white">
            Este producto es un kit (compuesto por otros SKU)
          </span>
        </label>

        {form.is_kit && (
          <KitComponentsEditor
            kitMode={form.kit_mode}
            components={form.components}
            onChange={set}
            candidates={kitCandidates}
            loading={loadingOptions}
            disabled={saving || !companyId}
          />
        )}

        <SectionTitle>Rotación, medidas y precio</SectionTitle>

        <Field
          label="Clase de rotación"
          htmlFor="product-rotation"
          hint="Decide qué tan cerca del punto de preparación conviene guardarlo."
        >
          <select
            id="product-rotation"
            data-testid="product-rotation"
            value={form.rotation_class}
            onChange={(event) => set({ rotation_class: event.target.value })}
            disabled={saving}
            className={inputClass}
          >
            {ROTATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ['weight', 'Peso (g)', '1'],
            ['height', 'Alto (cm)', 'any'],
            ['width', 'Ancho (cm)', 'any'],
            ['length', 'Largo (cm)', 'any'],
          ].map(([key, label, step]) => (
            <Field key={key} label={label} htmlFor={`product-${key}`}>
              <input
                id={`product-${key}`}
                type="number"
                min="0"
                step={step}
                data-testid={`product-${key}`}
                value={form[key]}
                onChange={(event) => set({ [key]: event.target.value })}
                disabled={saving}
                className={inputClass}
              />
            </Field>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <Field label="Precio" htmlFor="product-price">
            <input
              id="product-price"
              type="number"
              min="0"
              step="1"
              data-testid="product-price"
              value={form.price}
              onChange={(event) => set({ price: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>

          <div className="sm:pt-8">
            <Switch
              checked={form.is_active}
              onChange={(is_active) => set({ is_active })}
              label="Activo"
              ariaLabel="Producto activo"
              disabled={saving}
              testId="product-active"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="product-save"
        >
          {problem || 'Guardar'}
        </Button>
      </div>
    </Modal>
  );
};
